import { useRef, useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { motion, useInView } from "framer-motion";
import { FaWater } from "react-icons/fa";
import { FaPeopleGroup } from "react-icons/fa6";
import { PiMountains } from "react-icons/pi";
import { BsTree } from "react-icons/bs";
import { VscCompass } from "react-icons/vsc";
import CircularBadge from "../components/badge";
import "./CommunityHero.css";

/* ── Headline lines — two colours, matching design ─────────── */
const HEADLINE = ["Our Brotherhood", "Has No Borders."];

const RAIL_ICONS = [PiMountains, BsTree, FaWater, VscCompass];

const ease = [0.22, 1, 0.36, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.12 } },
};

const lineUp = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.85, ease } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease } },
};

/* ── Animated counter ───────────────────────────────────────── */
function useCounter(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTs = null;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(2, -10 * p);
      setVal(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return val;
}

export default function CommunityHero() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useCounter(1247, inView);

  return (
    <section className="cmhero" id="community-top" ref={ref}>
      {/* Background mountain image — same technique as home hero */}
      <div className="cmhero__media" aria-hidden="true">
        <motion.img src="./assets/hero.png" alt="" className="cmhero__img" initial={{ scale: 1.06, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.8, ease }} />
        {/* Left-side cream fade so text stays legible */}
        <div className="cmhero__scrim" />
      </div>

      {/* Animated cloud layer */}
      <div className="clouds" aria-hidden="true">
        <img src="./assets/image.png" alt="" />
        <img src="./assets/image.png" alt="" className="cloud-flip" />
      </div>

      {/* Circular badge — top right */}
      <div className="position-absolute cmhero__badge-pos" aria-hidden="true">
        <CircularBadge colors="var(--c-rust)" />
      </div>

      <Container fluid="xl" className="cmhero__inner container-fg container-2xxl">
        {/* Icon rail — identical to home hero */}
        <motion.aside className="hero__rail cmhero__rail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5, ease }} aria-hidden="true">
          <span className="hero__rail-num">01</span>
          <span className="hero__rail-line" />
          <ul className="hero__rail-icons">
            {RAIL_ICONS.map((Icon, i) => (
              <li key={i}>
                <Icon />
                <span className={`hero__rail-line ${i === RAIL_ICONS.length - 1 ? "" : "alt"}`} />
              </li>
            ))}
          </ul>
        </motion.aside>

        {/* Content */}
        <div className="cmhero__content">
          {/* Eyebrow */}
          <motion.p className="cmhero__eyebrow u-eyebrow" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.2 }}>
            Community Impact Map
          </motion.p>

          {/* Headline — both lines forest green (no rust split like home) */}
          <motion.h1 className="cmhero__title u-display" variants={container} initial="hidden" animate="show">
            {HEADLINE.map((line, i) => (
              <span className="hero__line" key={i}>
                <motion.span className="hero__line-inner" variants={lineUp} style={{ color: i === 1 ? "var(--c-rust)" : "" }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          {/* Rust rule */}
          <motion.span className="cmhero__rule" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.55 }} aria-hidden="true" />

          {/* Body copy */}
          <motion.p className="cmhero__sub" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.65 }}>
            Forge is building a nationwide community of veterans, first responders, and families who are choosing healing, purpose, and a path forward—together.
          </motion.p>

          {/* Lives impacted counter */}
          <motion.div className="cmhero__stat" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.78 }}>
            <span className="cmhero__stat-icon" aria-hidden="true">
              <FaPeopleGroup />
            </span>
            <div className="cmhero__stat-body">
              <span className="cmhero__stat-num">{count.toLocaleString()}</span>
              <span className="cmhero__stat-label">Lives Impacted and Growing</span>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
